import { Transition } from "@headlessui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { ArrowRight, FingerprintPattern, SquareAsterisk } from "lucide-react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";
import { authClient } from "@/auth";
import { AlertBar } from "@/components/AlertBar";
import { Button } from "@/components/catalyst/Button";
import { ErrorMessage, Field, Label } from "@/components/catalyst/Fieldset";
import { Input } from "@/components/catalyst/Input";
import { Switch, SwitchField } from "@/components/catalyst/Switch";
import { TextLink } from "@/components/catalyst/Text";
import { setBetterAuthError } from "@/hooks/use-better-auth-error";
import { useTurnstile } from "@/hooks/use-turnstile";
import { orpc } from "@/orpc/client";
import { type Schema, schema } from "./schema";

export function Form() {
	const router = useRouter();
	const { getCaptchaHeaders, reset: resetTurnstile } = useTurnstile();

	const {
		control,
		setError,
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<Schema>({
		resolver: zodResolver(schema),
	});

	const {
		mutate: sendPreflight,
		data: preflightData,
		isPending: isPreflightPending,
	} = useMutation(orpc.users.signinPreflight.mutationOptions());

	const email = useWatch({ name: "email", control });
	const setServerError = setBetterAuthError<Schema>(setError);

	const onSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
		if (!preflightData) {
			e.preventDefault();
			if (email) sendPreflight({ email });
			return;
		}
		handleSubmit(async (data) => {
			const response = await authClient.signIn.email({
				...data,
				fetchOptions: {
					headers: getCaptchaHeaders(),
					onError: (ctx) => {
						resetTurnstile();
						setServerError(ctx.error.code);
					},
				},
			});

			// Check for 2FA redirect
			if (
				response.data &&
				"twoFactorRedirect" in response.data &&
				response.data.twoFactorRedirect
			) {
				router.navigate({ to: "/auth/2fa" });
			} else if (response.data && !response.error) {
				router.navigate({ to: "/dashboard" });
			}
		})(e);
	};

	const handlePasskeySignIn = async () => {
		try {
			await authClient.signIn.passkey(
				{
					fetchOptions: {
						onSuccess: () => {
							router.navigate({ to: "/dashboard" });
						},
						onError: (ctx) => {
							setServerError(ctx.error.code);
						},
					},
				},
				{ throw: true },
			);
		} catch (e) {
			console.error(e);
			toast.error("Failed to sign in with passkey");
		}
	};

	return (
		<form className="flex flex-col gap-2" onSubmit={onSubmit}>
			{errors.root?.message && (
				<AlertBar title="Woops!" variant="error">
					{errors.root?.message}
				</AlertBar>
			)}

			<Field className="space-y-4">
				<div className="relative">
					<Input type="email" placeholder="Email" {...register("email")} />
					<Button
						outline
						className="absolute! right-0 top-0 rounded-l-none"
						disabled={!email || isPreflightPending}
						aria-label="Continue"
						onClick={() => sendPreflight({ email })}
					>
						<ArrowRight />
					</Button>
				</div>
				{errors.email?.message && (
					<ErrorMessage>{errors.email.message}</ErrorMessage>
				)}
			</Field>

			<Transition show={!!preflightData}>
				<div className="flex flex-col gap-4 transition duration-600 ease-in-out data-closed:opacity-0 data-closed:-translate-y-2">
					<Field className="space-y-1">
						<Input
							type="password"
							placeholder="Password"
							{...register("password")}
						/>
						<div className="text-right">
							<button
								type="button"
								className="text-xs text-base-content/50 hover:text-base-content hover:underline cursor-pointer"
								onClick={() => {
									authClient.requestPasswordReset({
										email,
										redirectTo: `${window.location.origin}/auth/password-reset`,
										fetchOptions: {
											headers: getCaptchaHeaders(),
											onSuccess: () => {
												toast.success("Check your email for the reset link.");
											},
											onError: (ctx) => {
												setServerError(ctx.error.code);
											},
										},
									});
								}}
							>
								Forgot password?
							</button>
						</div>
					</Field>

					<div className="flex flex-col sm:flex-row gap-4">
						<Button
							type="submit"
							className="flex-1 flex items-center gap-2"
							color="dark/white"
						>
							<SquareAsterisk className="size-4" />
							<div>Continue with Password</div>
						</Button>
						{preflightData?.hasPasskey && (
							<Button
								className="flex-1 flex items-center gap-2"
								onClick={handlePasskeySignIn}
								type="button"
								color="dark/white"
							>
								<FingerprintPattern className="size-4" />
								<div>Sign in with Passkey</div>
							</Button>
						)}
					</div>
				</div>
			</Transition>

			<div className="flex items-center justify-between">
				<SwitchField className="gap-4!">
					<Label className="text-xs font-semibold">Stay signed in</Label>
					<Controller
						control={control}
						name="rememberMe"
						render={({ field: { onChange, value } }) => (
							<Switch color="secondary" checked={value} onChange={onChange} />
						)}
					/>
				</SwitchField>

				<TextLink href="/auth/signup" className="text-sm">
					Create an account
				</TextLink>
			</div>
		</form>
	);
}
